import React, { Fragment, useEffect, useState } from "react";
import { bindActionCreators } from "redux";
import { connect } from "react-redux";
import { history } from "../../utils/history";
import {
  canManageRoles,
  canManagePermissions,
  canManageUsers,
  canManagePatients,
  roles as _roles,
} from "../../utils/constants";
import {
  getNameFromRoleId,
  getNameFromLocationId,
} from "../../utils/constants";
import {
  Grid,
  Table,
  TableBody,
  TableRow,
  TableCell,
  TablePagination,
  TableContainer,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";

import makeStyles from '@mui/styles/makeStyles';
import SearchField from "../shared/search-field";
import PageHeader from "../shared/page-header";

import {
  getPagedUsersSelector,
  getPagedUsersTotalSelector,
  searchUsers,
  clearUser,
  deleteUser,
  getRoles,
  getRolesSelector,
  getLocations,
  getUserFilterSelector,
  clearUserFilter,
  updateUserFilterItem,
  getFilteredLocationsSelector,
} from "../../store/admin";
import { getUserPermissions, setUserFromToken } from "../../store/user";
import { TablePaginationActions } from "../shared/pagination";
import { EnhancedTableHead } from "../shared/tableHeader";
import EditButton from "../shared/edit-button";
import DeleteButton from "../shared/delete-button/delete-button-view";
import auth from "../../services/authService";

const useStyles = makeStyles((theme) => ({
  button: theme.button,
}));

const UserManagement = ({ ...props }) => {
  const classes = useStyles();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [order, setOrder] = useState("asc");
  const [orderBy, setOrderBy] = useState("id");
  const [open, setOpen] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState(0);
  // Bumped whenever the data needs reloading (delete, etc.) without changing paging/sort/filter.
  const [reloadToken, setReloadToken] = useState(0);

  const { filter, rolePermissions } = props;

  // Extracted so the fetch effect's dependency array stays statically analyzable.
  const filterUsername = filter ? filter.username : "";
  const filterFirstName = filter ? filter.firstName : "";
  const filterLastName = filter ? filter.lastName : "";
  const filterLocationId = filter ? filter.locationId : 0;

  useEffect(() => {
    const init = async () => {
      const {
        clearUser,
        getRoles,
        getLocations,
        setUserFromToken,
        updateUserFilterItem,
      } = props;
      await getRoles();
      await getLocations();
      clearUser();

      const token = auth.getCurrentUser();

      if (token) {
        await setUserFromToken(token);
        updateUserFilterItem("locationId", parseInt(token.groupsid));
      }
    };
    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Server-side fetch. Filtering, sorting and paging all happen in SQL, so we only ever pull the
  // current page instead of the whole Users table. Text filters are debounced to avoid a request
  // per keystroke.
  useEffect(() => {
    if (!filter) return undefined;

    const patientsOnly =
      rolePermissions &&
      canManagePatients(rolePermissions) &&
      !canManageUsers(rolePermissions);

    const handle = setTimeout(() => {
      props.searchUsers({
        username: filterUsername || "",
        firstName: filterFirstName || "",
        lastName: filterLastName || "",
        locationId: filterLocationId || 0,
        roleId: patientsOnly ? _roles.patient : null,
        orderBy,
        order,
        skip: page * rowsPerPage,
        take: rowsPerPage,
      });
    }, 350);

    return () => clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    filterUsername,
    filterFirstName,
    filterLastName,
    filterLocationId,
    page,
    rowsPerPage,
    order,
    orderBy,
    reloadToken,
    rolePermissions,
  ]);

  const handleClose = () => {
    setOpen(false);
    setSelectedUserId(0);
  };

  const onDelete = (id) => {
    setSelectedUserId(id);
    setOpen(true);
  };

  const handleConfirmDelete = async () => {
    const { deleteUser, clearUser } = props;
    await deleteUser(selectedUserId);
    clearUser();
    setOpen(false);
    setReloadToken((t) => t + 1);
  };

  const handleChangePage = (e, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (e) => {
    setRowsPerPage(parseInt(e.target.value, 10));
    setPage(0);
  };

  const handleRequestSort = (e, property) => {
    const isDesc = orderBy === property && order === "desc";
    setOrder(isDesc ? "asc" : "desc");
    setOrderBy(property);
    setPage(0);
  };

  const onEdit = (id) => {
    history.push(`/user/${id}`);
  };

  const handleSearchChange = (e, name) => {
    const { updateUserFilterItem } = props;
    updateUserFilterItem(name, e.target.value);
    // Any new filter resets to the first page so the user sees matches from the top.
    setPage(0);
  };

  const doClear = async () => {
    const { clearUserFilter } = props;
    const token = await auth.getCurrentUser();
    await clearUserFilter(parseInt(token.groupsid));
    setPage(0);
  };

  const headCells = [
    { id: "id", numeric: false, disablePadding: false, label: "Id" },
    {
      id: "username",
      numeric: false,
      disablePadding: false,
      label: "Username",
    },
    {
      id: "firstName",
      numeric: false,
      disablePadding: false,
      label: "First Name",
    },
    {
      id: "lastName",
      numeric: false,
      disablePadding: false,
      label: "Last Name",
    },
    {
      id: "password",
      numeric: false,
      disablePadding: false,
      label: "Password",
    },
    { id: "roleId", numeric: false, disablePadding: false, label: "Role" },
    {
      id: "locationId",
      numeric: false,
      disablePadding: false,
      label: "Location",
    },
    { id: "edit", numeric: false, disablePadding: false, label: "" },
    { id: "delete", numeric: false, disablePadding: false, label: "" },
  ];

  const users = props.users || [];

  return (
    <Fragment>
      <PageHeader
        title="Users"
        description="Manage staff and patient accounts."
        action={
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
            <Button
              variant="contained"
              type="button"
              color="secondary"
              onClick={(e) => history.push(`/user/new`)}
            >
              Add User
            </Button>
            {rolePermissions && canManageRoles(rolePermissions) && (
              <Button
                variant="contained"
                type="button"
                color="primary"
                onClick={(e) => history.push(`/roles`)}
              >
                Roles
              </Button>
            )}
            {rolePermissions && canManagePermissions(rolePermissions) && (
              <Button
                variant="contained"
                type="button"
                color="primary"
                onClick={(e) => history.push(`/permissions`)}
              >
                Permissions
              </Button>
            )}
          </Stack>
        }
      />
      {filter && (
        <Grid container spacing={2}>
          <Grid item xs={12} sm={4}>
            <SearchField
              label="Search by username/email"
              value={filter.username}
              onChange={(e) => handleSearchChange(e, "username")}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <SearchField
              label="Search by first name"
              value={filter.firstName}
              onChange={(e) => handleSearchChange(e, "firstName")}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <SearchField
              label="Search by last name"
              value={filter.lastName}
              onChange={(e) => handleSearchChange(e, "lastName")}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <FormControl fullWidth size="small">
              <InputLabel id="locationId-label">Location</InputLabel>
              <Select
                labelId="locationId-label"
                id="locationId"
                label="Location"
                value={filter.locationId}
                onChange={(e) => handleSearchChange(e, "locationId")}
              >
                {props.locations &&
                  props.locations.map((location) => (
                    <MenuItem key={location.id} value={location.id}>
                      {location.name}
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      )}
      <Stack direction="row" spacing={2} sx={{ mt: 3, mb: 1 }}>
        <Button variant="outlined" color="primary" onClick={doClear}>
          Clear
        </Button>
      </Stack>
      <Grid item xs={12} style={{ overflow: "auto" }}>
        <TableContainer>
          <Table
            className={classes.table}
            size="small"
            aria-labelledby="users"
            aria-label="enhanced table"
          >
            <EnhancedTableHead
              classes={classes}
              order={order}
              orderBy={orderBy}
              onRequestSort={handleRequestSort}
              rowCount={users.length}
              headCells={headCells}
            />
            <TableBody>
              {users.map((user) => {
                return (
                  <TableRow key={user.id} className={classes.tableRow}>
                    <TableCell component="th" scope="row">
                      {user.id}
                    </TableCell>
                    <TableCell component="th" scope="row">
                      {user.username}
                    </TableCell>
                    <TableCell component="th" scope="row">
                      {user.firstName}
                    </TableCell>
                    <TableCell component="th" scope="row">
                      {user.lastName}
                    </TableCell>
                    <TableCell component="th" scope="row">
                      *****
                    </TableCell>
                    <TableCell component="th" scope="row">
                      {props.roles &&
                        getNameFromRoleId(user.roleId, props.roles)}
                    </TableCell>
                    <TableCell component="th" scope="row">
                      {props.locations &&
                        getNameFromLocationId(
                          user.locationId,
                          props.locations
                        )}
                    </TableCell>
                    <TableCell component="th" scope="row">
                      <EditButton pid={user.id} handleEdit={onEdit} />
                    </TableCell>
                    <TableCell component="th" scope="row">
                      <DeleteButton pid={user.id} handleDelete={onDelete} />
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 100]}
          component="div"
          count={props.total || 0}
          rowsPerPage={rowsPerPage}
          page={page}
          SelectProps={{
            inputProps: { "aria-label": "rows per page" },
            native: true,
          }}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          ActionsComponent={TablePaginationActions}
        />
      </Grid>
      <Dialog
        open={open}
        onClose={handleClose}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">
          {"Confirm Delete User"}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            Are you sure you want to delete selected user?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            variant="contained"
            onClick={handleConfirmDelete}
            color="primary"
          >
            Yes
          </Button>
          <Button
            variant="contained"
            onClick={handleClose}
            color="primary"
            autoFocus
          >
            No
          </Button>
        </DialogActions>
      </Dialog>
    </Fragment>
  );
};

const mapStateToProps = (state) => {
  return {
    rolePermissions: getUserPermissions(state),
    users: getPagedUsersSelector(state),
    total: getPagedUsersTotalSelector(state),
    roles: getRolesSelector(state),
    locations: getFilteredLocationsSelector(state),
    filter: getUserFilterSelector(state),
  };
};

const mapDispatchToProps = (dispatch) => {
  return {
    ...bindActionCreators(
      {
        searchUsers,
        clearUser,
        deleteUser,
        getRoles,
        getLocations,
        updateUserFilterItem,
        clearUserFilter,
        setUserFromToken,
      },
      dispatch
    ),
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(UserManagement);
